import { logAnalyticsEvent } from "./functions";

const SESSION_STORAGE_KEY = "v2_analytics_session_id";

const isBrowser = typeof window !== "undefined";

const generateSessionId = () => {
  if (!isBrowser) return undefined;
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getSessionId = () => {
  if (!isBrowser) return undefined;
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const sessionId = generateSessionId();
    if (sessionId) {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch (error) {
    console.error("Failed to access session storage", error);
    return undefined;
  }
};

type TrackEventParams = {
  eventName: string;
  params?: Record<string, unknown>;
};

const trackEvent = async ({ eventName, params }: TrackEventParams) => {
  try {
    const sessionId = getSessionId();
    await logAnalyticsEvent({
      eventName,
      params,
      sessionId: sessionId || undefined,
    });
  } catch (error) {
    console.error(`Failed to track analytics event ${eventName}`, error);
  }
};

const trackPageView = async (path: string, additional?: Record<string, unknown>) => {
  await trackEvent({
    eventName: "page_view",
    params: {
      page_path: path,
      ...(additional || {}),
    },
  });
};

const trackCtaClick = async (
  identifier: string,
  additional?: Record<string, unknown>
) => {
  await trackEvent({
    eventName: "cta_click",
    params: {
      identifier,
      ...(additional || {}),
    },
  });
};

const trackCheckoutStarted = async (
  checkoutId?: string,
  additional?: Record<string, unknown>
) => {
  await trackEvent({
    eventName: "checkout_started",
    params: {
      checkout_id: checkoutId,
      ...(additional || {}),
    },
  });
};

const trackCheckoutCompleted = async (
  checkoutId?: string,
  additional?: Record<string, unknown>
) => {
  await trackEvent({
    eventName: "checkout_completed",
    params: {
      checkout_id: checkoutId,
      ...(additional || {}),
    },
  });
};

const trackCheckoutError = async (
  checkoutId?: string,
  errorMessage?: string
) => {
  await trackEvent({
    eventName: "checkout_error",
    params: {
      checkout_id: checkoutId,
      message: errorMessage,
    },
  });
};

export {
  trackPageView,
  trackCtaClick,
  trackCheckoutStarted,
  trackCheckoutCompleted,
  trackCheckoutError,
};
